import { http } from "Utils/http";
import { STATUSES } from "Utils/constants";
import { httpDownload } from "Utils/httpDownload";

export const DS_INIT_URL = "/api/report/ds/initLoad";
export const DS_VALIDATE_URL = "/api/report/ds/validate";
export const DS_GENERATE_URL = "/api/report/ds/generate";

const loadDSFormInfo = async (
  ACTIONS,
  formInfoDispatch,
  handleRadioChange,
  handleSelectChange,
  handleCheckboxChange,
  handleDateChange,
  handleTextChange,
  token
) => {
  formInfoDispatch({
    type: ACTIONS.SET_INITIAL_DATA,
    data: "",
    loading: true
  });

  let status = STATUSES.SUCCESS;

  await http
    .post(
      DS_INIT_URL,
      {},
      { headers: { Authorization: token } }
    )
    .then((res) => {
      const payload = res?.data ?? res;

      if (res?.status !== 1 || (res?.globalErrorMessageList?.length > 0)) {
        status = STATUSES.ERROR;
        return Promise.reject({ status, data: res });
      }

      const psoList = Array.isArray(payload?.psoList) ? payload.psoList : [];
      const cfSettingList = Array.isArray(payload?.cfSettingList) ? payload.cfSettingList : [];

      const orgOptions = [
        {
          index: -1,
          id: -1,
          name: "Select all organizations",
          value: "-1",
          displayValue: "Select all organizations",
          title: "Select all organizations",
          isSelected: true
        },
        ...psoList.map((o) => ({
          index: o.id,
          id: o.id,
          name: o.name,
          psoId: o.psoId,
          value: String(o.id),
          displayValue: o.psoId ? `${o.name} (${o.psoId})` : `${o.name}`,
          title: `Select ${o.name}`,
          isSelected: false
        }))
      ];

      const cfCheckBoxes = cfSettingList.map((c) => ({
        id: c.id,
        name: "cfSettings",
        label: c.name,
        value: String(c.id),
        description: c.description,
        isChecked: false,
        onChange: handleCheckboxChange,
        disabled: false
      }));

      const submissionTypeBoxes = [
        {
          id: "submissionTypes_Y",
          name: "submissionTypes",
          label: "Test",
          value: "Y",
          isChecked: false,
          onChange: handleCheckboxChange,
          disabled: false
        },
        {
          id: "submissionTypes_N",
          name: "submissionTypes",
          label: "Production",
          value: "N",
          isChecked: false,
          onChange: handleCheckboxChange,
          disabled: false
        }
      ];

      const data = {
        reportType: {
          value: "Summary",
          radioButtons: [
            {
              id: "reportType_Summary",
              name: "reportType",
              value: "Summary",
              label: "Summary Report",
              onChange: handleRadioChange,
              isChecked: true
            },
            {
              id: "reportType_Detail",
              name: "reportType",
              value: "Detail",
              label: "Detail Report",
              onChange: handleRadioChange,
              isChecked: false
            }
          ],
          validation: { required: true, isInvalid: false, errors: [] },
          disabled: false
        },
        organization: {
          value: "-1",
          options: orgOptions,
          validation: { required: true, isInvalid: false, errors: [] },
          onChange: handleSelectChange,
          disabled: false
        },
        startDate: {
          value: "",
          validation: { required: true, isInvalid: false, errors: [] },
          onChange: handleDateChange,
          disabled: false
        },
        endDate: {
          value: "",
          validation: { required: true, isInvalid: false, errors: [] },
          onChange: handleDateChange,
          disabled: false
        },
        cfSettings: {
          type: "checkbox",
          checkBoxes: cfCheckBoxes,
          validation: { required: true, isInvalid: false, errors: [] },
          disabled: false
        },
        submissionTypes: {
          type: "checkbox",
          checkBoxes: submissionTypeBoxes,
          validation: { required: true, isInvalid: false, errors: [] },
          disabled: false
        },
        fileName: {
          value: "Patient Safety Data Submission Report",
          validation: { required: true, isInvalid: false, errors: [] },
          onChange: handleTextChange,
          disabled: false
        }
      };

      formInfoDispatch({
        type: ACTIONS.SET_INITIAL_DATA,
        data,
        loading: false,
        selectOrg: payload.vendorUser || payload.psoppcAdmin
      });
    })
    .catch(function (error) {
      status = STATUSES.ERROR;
      return Promise.reject({ status, data: error });
    });
};

const validateDSRpt = async (
  token,
  formInfo,
  nameConverter,
  addErrorMessages,
  clearErrorMessages,
  getFieldValue
) => {
  clearErrorMessages(formInfo.data);

  const org = getFieldValue("organization", formInfo.data);
  const cf = getFieldValue("cfSettings", formInfo.data);
  const submissionTypes = getFieldValue("submissionTypes", formInfo.data);

  const body = {
    organizationId: org?.id ?? -1,
    orgName: org?.name ?? "",
    cfSettingIds: cf?.ids ?? [],
    cfSettingNames: cf?.labels ?? [],
    startDate: getFieldValue("startDate", formInfo.data),
    endDate: getFieldValue("endDate", formInfo.data),
    reportType: getFieldValue("reportType", formInfo.data),
    reportFileName: getFieldValue("fileName", formInfo.data),
    submissionTypes: submissionTypes ?? []
  };

  let status = STATUSES.SUCCESS;

  const res = await http
    .post(DS_VALIDATE_URL, body, { headers: { Authorization: token } })
    .then((res) => {
      const payload = res?.data ?? res;

      let x = 0;
      for (const [serverKey, uiKey] of Object.entries(nameConverter)) {
        const fieldErrObj = payload.fieldErrors?.[serverKey];
        if (fieldErrObj) {
          addErrorMessages(uiKey, serverKey, payload, x === 0);
          status = STATUSES.ERROR;
          x++;
        }
      }
      return Promise.resolve({ status, data: payload });
    })
    .catch(function (error) {
      status = STATUSES.ERROR;
      return Promise.reject({ status, data: error });
    });

  return res;
};

const generateDSRpt = async (formInfo, token, getFieldValue) => {
  let status = STATUSES.SUCCESS;

  const org = getFieldValue("organization", formInfo.data);
  const cf = getFieldValue("cfSettings", formInfo.data);
  const submissionTypes = getFieldValue("submissionTypes", formInfo.data);

  const body = {
    organizationId: org?.id ?? -1,
    orgName: org?.name ?? "",
    cfSettingIds: cf?.ids ?? [],
    cfSettingNames: cf?.labels ?? [],
    startDate: getFieldValue("startDate", formInfo.data),
    endDate: getFieldValue("endDate", formInfo.data),
    reportType: getFieldValue("reportType", formInfo.data),
    reportFileName: getFieldValue("fileName", formInfo.data),
    submissionTypes: submissionTypes ?? []
  };

  const res = await httpDownload
    .post(DS_GENERATE_URL, body, {
      responseType: "blob",
      headers: { Authorization: token }
    })
    .then((res) => {
      if (res?.status !== 200 || (res?.globalErrorMessageList?.length > 0)) {
        status = STATUSES.ERROR;
        return Promise.reject({ status, data: res });
      }
      const headers = res.headers ?? undefined;
      const blob = res.data ?? res;
      return Promise.resolve({ status, data: { headers, data: blob } })
    })
    .catch(async function (error) {
      status = STATUSES.ERROR;
      let parsedData = null;
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          parsedData = JSON.parse(text);
        } catch (_) { }
      }
      return Promise.reject({ status, data: { err: error, data: parsedData ?? error.response } });
    });

  return res;
};

export { loadDSFormInfo, validateDSRpt, generateDSRpt };
